import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { ReminderCreationActionData } from 'src/app/components/designer/models/reminder-creation-action-data';
import { FormMode } from '../../common/form-mode';
import { DateReferences } from 'src/app/models/enums/date-references';
import { WorkflowDataInterface } from '../workflow-data.interface';
import { Subject } from 'rxjs';
import { ContextualMenuEvent } from '../contextual-menu-event';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReminderActionType } from 'src/app/models/RestApiCaller/Enums/reminder-action-type';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { Attribute } from 'src/app/models/attribute';
import { PropertyType } from 'src/app/models/enums/property-type';
import { NeedEmailTemplates } from '../need-email-templates';
import { EmailTemplate } from 'src/app/models/email-template';

@Component({
    selector: 'app-workflow-action-reminder-creation-data',
    imports: [ReactiveFormsModule, MatFormFieldModule, FormsModule, MatSelectModule, FaIconComponent, MatTooltipModule, MatCheckboxModule, MatInputModule],
    templateUrl: './workflow-action-reminder-creation-data.component.html',
    styleUrl: './workflow-action-reminder-creation-data.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowActionReminderCreationDataComponent implements WorkflowDataInterface, OnInit, NeedEmailTemplates {
    private attributeDefinitionsService = inject(AttributeDefinitionsService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    public faCircleInfo = faCircleInfo;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: ReminderCreationActionData;

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    set data(value: string | ReminderCreationActionData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as ReminderCreationActionData;
        }

        this.fillForm();
    }

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    get data(): ReminderCreationActionData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    public DateReferences = DateReferences;
    public ReminderActionType = ReminderActionType;

    attributesTypeDate: Attribute[] = [];

    emailTemplates: EmailTemplate[] = [];

    /**
     * Liste d'éléments utilisés par les liste déroulantes du template.
     */
    public DateReferencesList: { value: DateReferences; name: string }[] = [
        { value: DateReferences.FolderCreation, name: 'Création du dossier' },
        { value: DateReferences.LastFolderUpdate, name: 'Dernière modification du dossier' },
        { value: DateReferences.DateAttribute, name: 'Attribut de type date' },
    ];

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    public frequencyPattern = new RegExp(/^(\d+[d,j]{1})?\s*(\d+[h]{1})?\s*(\d+[m]{1})?$/);

    private partRegex = new RegExp(/^(?<count>\d+)(?<unit>[d,j,h,m]{1})$/);

    public maxOccurencesPattern: string = '^[0-9]+$';

    constructor() {
        this.dataForm = new UntypedFormGroup({
            dateReference: new UntypedFormControl(null, Validators.required),
            attributeId: new UntypedFormControl(null, Validators.required),
            delayBeforeTriggering: new UntypedFormControl(null, Validators.pattern(this.frequencyPattern)),
            isRepeated: new UntypedFormControl(false),
            frequencyFormControl: new UntypedFormControl(null, [Validators.required, Validators.pattern(this.frequencyPattern)]),
            maxOccurencesFormControl: new UntypedFormControl(null, Validators.pattern(this.maxOccurencesPattern)),
            reminderActionTypeFormControl: new UntypedFormControl(null, Validators.required),
            emailTemplate: new UntypedFormControl(null, Validators.required),
            notificationTitle: new UntypedFormControl(null, Validators.required),
            notificationText: new UntypedFormControl(null, Validators.required),
        });

        this.attributeDefinitionsService.List(null, null, null, null).subscribe((result) => {
            this.attributesTypeDate = result.value.filter((x) => x.type === PropertyType.Date && x.custom === true);
            this.dataForm.get('attributeId').updateValueAndValidity();
            this.changeDetectorRef.markForCheck();
        });

        this.dataForm.get('isRepeated').valueChanges.subscribe((value) => {
            if (value) {
                this.dataForm.get('frequencyFormControl').enable();
                this.dataForm.get('maxOccurencesFormControl').enable();
            } else {
                this.dataForm.get('frequencyFormControl').disable();
                this.dataForm.get('maxOccurencesFormControl').disable();
            }
        });
    }

    set templates(value: EmailTemplate[]) {
        this.emailTemplates = value;
    }

    ngOnInit(): void {
        if (this.mode === FormMode.Reading) {
            this.dataForm.disable();
        } else {
            this.dataForm.enable();
        }

        this.fillForm();
    }

    get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    isValid(): boolean {
        return false;
    }

    clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Emis lorsque la durée est modifiée (perte de focus)
     * @param $event Événement.
     */
    public onDurationChange(formcontrolName: string): void {
        if (this.IsFrequencyDurationValid(formcontrolName) === true) {
            this.dataForm.get(formcontrolName).setErrors(null);
        } else {
            this.dataForm.get(formcontrolName).setErrors({ pattern: true });
        }
    }

    /**
     * Retourne une valeur indiquant si la durée de mise en veille est valide.
     */
    private IsFrequencyDurationValid(formcontrolName: string): boolean {
        let isValid = true;

        const durationTextRepresentation: string = this.dataForm.get(formcontrolName).value;

        if (!durationTextRepresentation) {
            return false;
        }

        const durationArray = durationTextRepresentation.trim().split(' ');

        if (durationArray.length !== 0) {
            durationArray.forEach((part) => {
                if (this.partRegex.test(part.trim()) === false) {
                    isValid = false;
                }
            });
        } else {
            isValid = false;
        }

        return isValid;
    }

    /**
     * Retourne la durée de fréquence en minutes
     */
    private getFrequencyDuration(value: string): number {
        const durationTextRepresentation: string = value;

        if (!durationTextRepresentation) {
            return null;
        }

        const durationArray = durationTextRepresentation.trim().split(' ');

        let duration = 0;

        if (durationArray.length !== 0) {
            durationArray.forEach((part) => {
                let result = this.partRegex.exec(part.trim());
                const count = result.groups['count'];
                const unit = result.groups['unit'];

                switch (unit) {
                    case 'd':
                    case 'j':
                        duration += +count * (60 * 24);
                        break;
                    case 'h':
                        duration += +count * 60;
                        break;
                    case 'm':
                        duration += +count;
                        break;
                }
            });
        }

        return duration;
    }

    private getFormatTime(time: number): string {
        if (!time || time === 0) {
            return '';
        }

        const minuts = time % 60;
        const totalHours = Math.floor(time / 60);
        const remainingHours = totalHours % 24;
        const totalDays = Math.floor(totalHours / 24);
        const remainingDays = totalDays % 7;
        const weeks = Math.floor(totalDays / 7);

        let format: string = '';

        if (weeks > 0) {
            format += `${weeks}s`;
        }

        if (remainingDays > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${remainingDays}j`;
        }

        if (remainingHours > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${remainingHours}h`;
        }

        if (minuts > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${minuts}m`;
        }

        return format;
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm && this._data) {
            this.dataForm.get('dateReference').setValue(this._data.dateReference);
            this.dataForm.get('attributeId').setValue(this._data.dateAttributeId);
            this.dataForm.get('delayBeforeTriggering').setValue(this.getFormatTime(this._data.delay));
            this.dataForm.get('isRepeated').setValue(this._data.occurenceFrequency != null);
            this.dataForm.get('frequencyFormControl').setValue(this.getFormatTime(this._data.occurenceFrequency));
            this.dataForm.get('maxOccurencesFormControl').setValue(this._data.maxOccurence);
            this.dataForm.get('reminderActionTypeFormControl').setValue(this._data.actionType);
            if (this._data.actionType === ReminderActionType.SendMail) {
                let emailActionData = JSON.parse(this._data.actionData);
                this.dataForm.get('emailTemplate').setValue(emailActionData.templateId);
            } else {
                let notificationActionData = this._data.actionData ? JSON.parse(this._data.actionData) : null;
                this.dataForm.get('notificationTitle').setValue(notificationActionData?.title);
                this.dataForm.get('notificationText').setValue(notificationActionData?.text);
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: ReminderCreationActionData = new ReminderCreationActionData();
        updatedData.dateReference = this.dataForm.get('dateReference').value;
        updatedData.dateAttributeId = this.dataForm.get('attributeId').value;
        updatedData.delay = this.getFrequencyDuration(this.dataForm.get('delayBeforeTriggering').value);
        updatedData.occurenceFrequency = this.getFrequencyDuration(this.dataForm.get('frequencyFormControl').value);
        updatedData.maxOccurence = this.dataForm.get('maxOccurencesFormControl').value as number;
        updatedData.actionType = this.dataForm.get('reminderActionTypeFormControl').value;
        if (updatedData.actionType === ReminderActionType.SendMail) {
            let emailActionData = { templateId: this.dataForm.get('emailTemplate')?.value };
            updatedData.actionData = JSON.stringify(emailActionData);
        } else {
            let notificationActionData = { title: this.dataForm.get('notificationTitle')?.value, text: this.dataForm.get('notificationText')?.value };
            updatedData.actionData = JSON.stringify(notificationActionData);
        }

        this._data = updatedData;
    }
}
